import { useEffect, useId, useRef } from "react";
import { USE_DEMO_DATA } from "../lib/api";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          theme: "auto";
          callback: (token: string) => void;
          "expired-callback": () => void;
        }
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Không tải được Turnstile."));
    document.head.append(script);
  });
  return scriptPromise;
}

export function TurnstileWidget({
  onToken
}: {
  onToken: (token: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<string | null>(null);
  const descriptionId = useId();

  useEffect(() => {
    if (USE_DEMO_DATA) {
      onToken("demo-turnstile-token");
      return;
    }

    let active = true;
    const render = async () => {
      await loadTurnstile();
      if (!active || !containerRef.current || !window.turnstile) return;
      widgetRef.current = window.turnstile.render(containerRef.current, {
        sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "",
        theme: "auto",
        callback: onToken,
        "expired-callback": () => onToken("")
      });
    };
    void render();

    return () => {
      active = false;
      if (widgetRef.current && window.turnstile) {
        window.turnstile.remove(widgetRef.current);
      }
    };
  }, [onToken]);

  return (
    <div aria-describedby={descriptionId}>
      {USE_DEMO_DATA ? (
        <div className="flex min-h-12 items-center gap-2 rounded-xl border border-dashed border-primary/35 bg-primary/5 px-3 text-xs text-muted-foreground">
          <span className="size-2 rounded-full bg-primary" />
          Turnstile đang dùng khóa kiểm thử local
        </div>
      ) : (
        <div ref={containerRef} className="min-h-16" />
      )}
      <span id={descriptionId} className="sr-only">
        Xác minh chống bot bằng Cloudflare Turnstile.
      </span>
    </div>
  );
}
